import { useState, useEffect, useRef, useCallback } from "react";
import { useTerminology } from "@/contexts/TerminologyContext";
import { useTranslation } from "react-i18next";
import { Globe, Building2, Users, DollarSign, Phone, Loader2, ArrowLeft } from "lucide-react";
import LinkedInIcon from "@/components/shared/LinkedInIcon";
import { enrichCompany } from "@/lib/enrichment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { companies } from "@/data/dummy";
import { useCreateAccountMutation, useGetIndustriesLazyQuery } from "@/lib/graphql/types-and-hooks";
import { EmployeeRanges, RevenueRanges, displayRangeLabel, findMatchingRange, type RangeType } from "@/lib/types/ranges";
import EditableLocationField from "@/components/shared/EditableLocationField";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { toast } from "sonner";
import { isLovablePreview } from "@/lib/utils/environment";

interface AddCompanyFormProps {
  onCompanyCreated: (company: { id: string; name: string; website: string; location: string; industry: string; employees: string; revenue: string; phone: string; linkedin: string }) => void;
  onCancel: () => void;
  initialName?: string;
  initialData?: Record<string, string>;
  showBackButton?: boolean;
}

const domainCompanyMap: Record<string, { name: string; website: string; industry?: string; location?: string; employees?: string }> = {};
companies.forEach(c => {
  const domain = c.website?.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
  if (domain) domainCompanyMap[domain] = { name: c.name, website: c.website, industry: (c as any).industry, location: (c as any).location, employees: (c as any).employees };
});

function extractDomain(url: string): string {
  return url.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0].toLowerCase();
}

function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
}

function guessCompanyName(domain: string): string {
  const known = domainCompanyMap[domain];
  if (known) return known.name;
  const name = domain.split(".")[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function normalizeLinkedin(value: string): string {
  return value
    .replace(/^https?:\/\/(www\.)?linkedin\.com\/(company\/|in\/)?/, "")
    .replace(/\/$/, "")
    .trim();
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  const national = digits.startsWith("1") && digits.length > 10 ? digits.slice(1) : digits;
  if (national.length <= 3) return national;
  if (national.length <= 6) return `(${national.slice(0, 3)}) ${national.slice(3)}`;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6, 10)}`;
}

// Buckets, not strings: the Select's VALUE stays the vendor-canonical
// `storedLabel` (it is written to the account record), while the option text is
// formatted for the reader's locale.
const employeeRanges = EmployeeRanges.filter(r => !!r.labelKey);

// Map raw backend values (e.g. "250", "$10M-$50M") to the exact Select labels.
function normalizeRangeLabel(raw: string, ranges: RangeType[]): string {
  const value = raw.trim();
  if (!value) return "";
  const labels = ranges.map(r => r.storedLabel);
  if (labels.includes(value)) return value;
  const dashNormalized = value.replace(/\s*[-–—]\s*/g, " to ");
  if (labels.includes(dashNormalized)) return dashNormalized;
  const digits = value.replace(/[^\d.]/g, "");
  const numeric = digits ? parseFloat(digits) : NaN;
  if (Number.isFinite(numeric)) {
    const isRevenue = /[$M|B|K]/i.test(value);
    const scale = /B/i.test(value) ? 1_000_000_000 : /M/i.test(value) ? 1_000_000 : /K/i.test(value) ? 1_000 : 1;
    const resolved = numeric * scale;
    const label = findMatchingRange(resolved, resolved, ranges);
    if (label) return label;
    void isRevenue;
  }
  return "";
}
const revenueRanges = RevenueRanges.filter(r => !!r.labelKey);

export default function AddCompanyForm({ onCompanyCreated, onCancel, initialName = "", initialData, showBackButton = false }: AddCompanyFormProps) {
  const { terminology, singular } = useTerminology();
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const [createAccountMutation, { loading: creating }] = useCreateAccountMutation();
  const [getIndustries, { loading: industriesLoading }] = useGetIndustriesLazyQuery();

  const [url, setUrl] = useState("");
  const [domain, setDomain] = useState("");
  const [name, setName] = useState(initialName);
  const [nameEdited, setNameEdited] = useState(!!initialName);
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [industrySearch, setIndustrySearch] = useState("");
  const [industryOpen, setIndustryOpen] = useState(false);
  const [employees, setEmployees] = useState("");
  const [revenue, setRevenue] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [faviconError, setFaviconError] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const enrichedRef = useRef(false);

  // Pre-fill from initialData prop (passed from Slack "Add Company" link)
  useEffect(() => {
    if (!initialData) return;
    if (initialData.name) { setName(initialData.name); setNameEdited(true); }
    if (initialData.domain) { setUrl(initialData.domain); setDomain(initialData.domain); }
    if (initialData.industry) setIndustry(initialData.industry);
    if (initialData.employeeCount) setEmployees(normalizeRangeLabel(initialData.employeeCount, EmployeeRanges));
    if (initialData.linkedin) setLinkedin(initialData.linkedin);
    if (initialData.phone) setPhone(formatPhone(initialData.phone));
    if (initialData.revenue) setRevenue(normalizeRangeLabel(initialData.revenue, RevenueRanges));
    if (initialData.city || initialData.state || initialData.country) {
      setLocation([initialData.city, initialData.state, initialData.country].filter(Boolean).join(", "));
    }
  }, [initialData]);

  const [industrySuggestions, setIndustrySuggestions] = useState<string[]>([]);

  const fetchIndustries = useCallback(async (search: string) => {
    if (isLovablePreview()) {
      const opts = [...new Set(companies.map(c => (c as any).industry).filter(Boolean).filter((v: string) => v !== "-"))].sort() as string[];
      setIndustrySuggestions(search ? opts.filter(i => i.toLowerCase().includes(search.toLowerCase())) : opts);
      return;
    }
    if (!workspaceId) return;
    try {
      const res = await getIndustries({
        fetchPolicy: "cache-and-network",
        variables: { filter: { workspaceId: workspaceId.toString(), search } },
      });
      const labels = res.data?.industries?.edges?.map((e: any) => e?.node?.label).filter(Boolean) ?? [];
      setIndustrySuggestions(labels);
    } catch {
      setIndustrySuggestions([]);
    }
  }, [workspaceId, getIndustries]);

  useEffect(() => {
    if (!industryOpen) return;
    const timer = setTimeout(() => fetchIndustries(industrySearch), 200);
    return () => clearTimeout(timer);
  }, [industrySearch, industryOpen, fetchIndustries]);

  useEffect(() => {
    const d = extractDomain(url);
    if (d && d.includes(".")) {
      setDomain(d);
      setFaviconError(false);
      const known = domainCompanyMap[d];
      if (!nameEdited) setName(guessCompanyName(d));
      if (known) {
        if (known.industry) setIndustry(known.industry);
        if (known.location) setLocation(known.location);
        if (known.employees) setEmployees(known.employees);
      }
    } else {
      setDomain("");
    }
  }, [url, nameEdited]);

  const handleUrlBlur = useCallback(async () => {
    const d = extractDomain(url);
    if (!d || !d.includes(".") || enrichedRef.current) return;
    enrichedRef.current = true;
    setEnriching(true);
    try {
      const data = await enrichCompany(d, name || guessCompanyName(d));
      if (data) {
        if (data.name && !nameEdited) setName(data.name);
        if (data.industry && !industry) setIndustry(data.industry);
        if (data.employees && !employees) setEmployees(data.employees);
        if (data.revenue && !revenue) setRevenue(data.revenue);
        if (data.location && !location) setLocation(data.location);
        if (data.phone && !phone) setPhone(formatPhone(data.phone));
        if (data.linkedin && !linkedin) setLinkedin(data.linkedin);
      }
    } catch (e) {
      console.error("Enrichment failed:", e);
    } finally {
      setEnriching(false);
    }
  }, [url, name, nameEdited, industry, employees, revenue, location, phone, linkedin]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    // In preview mode, return fake data
    if (isLovablePreview()) {
      onCompanyCreated({
        id: `new-${Date.now()}`,
        name: name.trim(),
        website: domain || url.trim(),
        location: location.trim(),
        industry: industry.trim(),
        employees: employees.trim(),
        revenue: revenue.trim(),
        phone: phone.trim(),
        linkedin: linkedin.trim() ? `linkedin.com/company/${linkedin.trim()}` : "",
      });
      return;
    }

    // Create company in backend
    try {
      const locationParts = location.split(',').map(s => s.trim());

      const result = await createAccountMutation({
        variables: {
          input: {
            domain: domain || url.trim(), // Domain at top level (matches coffee-ui)
            workspaceId: workspaceId as any,
            properties: {
              name: name.trim(),
              city: locationParts[0] || undefined,
              state: locationParts[1] || undefined,
              country: locationParts[2] || undefined,
              industry: industry.trim() || undefined,
              phone: phone.trim() || undefined,
              linkedinUrl: linkedin.trim() ? `linkedin.com/company/${linkedin.trim()}` : undefined,
            },
          },
        },
        refetchQueries: ['getAccounts'],
        awaitRefetchQueries: true,
      });

      if (result.data?.createAccount?.account) {
        const account = result.data.createAccount.account;
        onCompanyCreated({
          id: account.id,
          name: name.trim(),
          website: domain || url.trim(),
          location: location.trim(),
          industry: industry.trim(),
          employees: employees.trim(),
          revenue: revenue.trim(),
          phone: phone.trim(),
          linkedin: linkedin.trim(),
        });

        toast.success(t("common.entityCreated", { entity: singular.companies }));
      } else {
        // Thrown and re-shown by the catch below, so it has to be translated copy
        // rather than a developer string.
        throw new Error(t("forms.company.createFailed"));
      }
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(error instanceof Error ? error.message : t("forms.company.createFailed"));
    }
  };

  return (
    <>
      <div className="px-6 pt-6 pb-4 border-b border-border flex items-center gap-3">
        {showBackButton && (
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground transition">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <h2 className="text-lg font-display">{t("common.addEntity", { entity: singular.companies })}</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
        {/* URL input */}
        <div className="space-y-1.5">
          <Label htmlFor="company-url" className="text-sm">{t("forms.company.websiteUrl")} <span className="text-destructive">*</span></Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="company-url"
              value={url}
              onChange={e => { setUrl(e.target.value); enrichedRef.current = false; }}
              onBlur={handleUrlBlur}
              placeholder={t("forms.company.websitePlaceholder")}
              className="pl-9"
              autoFocus
            />
            {enriching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          {enriching && (
            <p className="text-xs text-muted-foreground animate-pulse">{t("forms.company.lookingUp")}</p>
          )}
          {url.trim() && !domain && !enriching && (
            <p className="text-xs text-destructive">{t("forms.company.invalidWebsite")}</p>
          )}
        </div>

        {/* Company name */}
        <div className="space-y-1.5">
          <Label htmlFor="company-name" className="text-sm">{t("forms.company.nameLabel")}</Label>
          <div className="flex items-center gap-3">
            {domain && !faviconError ? (
              <img
                src={faviconUrl(domain)}
                alt={name}
                className="h-8 w-8 rounded object-contain shrink-0"
                onError={() => setFaviconError(true)}
              />
            ) : domain ? (
              <div className="h-8 w-8 rounded bg-accent/10 flex items-center justify-center text-sm font-bold text-accent shrink-0">
                {name.charAt(0).toUpperCase()}
              </div>
            ) : null}
            <div className="relative flex-1">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="company-name"
                value={name}
                onChange={e => { setName(e.target.value); setNameEdited(true); }}
                placeholder={t("forms.company.namePlaceholder")}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {/* Two-column fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            {/* Standard field labels come from the shared field catalog, not a second copy. */}
            <Label className="text-sm">{t("fields.companies.location")}</Label>
            <EditableLocationField
              value={location}
              onChange={setLocation}
              className="h-9 rounded-md border border-input bg-background px-3 hover:border-accent/50 transition-colors"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="company-industry" className="text-sm">{t("fields.companies.industry")}</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
              <Input
                id="company-industry"
                value={industry || industrySearch}
                onChange={e => { setIndustrySearch(e.target.value); setIndustry(""); setIndustryOpen(true); }}
                onFocus={() => setIndustryOpen(true)}
                onBlur={() => setTimeout(() => setIndustryOpen(false), 150)}
                placeholder={t("forms.company.industryPlaceholder")}
                className="pl-9"
              />
              {industriesLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />}
              {industryOpen && industrySuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md z-50 max-h-40 overflow-y-auto">
                  {industrySuggestions.map((opt: string) => (
                    <button
                      key={opt}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition"
                      onMouseDown={e => { e.preventDefault(); setIndustry(opt); setIndustrySearch(""); setIndustryOpen(false); }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">{t("fields.companies.employees")}</Label>
            <Select value={employees} onValueChange={setEmployees}>
              <SelectTrigger className="w-full">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <SelectValue placeholder={t("forms.company.selectRange")} />
                </div>
              </SelectTrigger>
              <SelectContent>
                {employeeRanges.map(r => (
                  <SelectItem key={r.id} value={r.storedLabel}>{displayRangeLabel(r, t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">{t("forms.company.revenue")}</Label>
            <Select value={revenue} onValueChange={setRevenue}>
              <SelectTrigger className="w-full">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <SelectValue placeholder={t("forms.company.selectRange")} />
                </div>
              </SelectTrigger>
              <SelectContent>
                {revenueRanges.map(r => (
                  <SelectItem key={r.id} value={r.storedLabel}>{displayRangeLabel(r, t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="company-phone" className="text-sm">{t("fields.companies.phone")}</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="company-phone"
                value={phone}
                onChange={e => setPhone(formatPhone(e.target.value))}
                placeholder={t("forms.company.phonePlaceholder")}
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="company-linkedin" className="text-sm">LinkedIn</Label>
            <div className="relative flex items-center">
              <LinkedInIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" />
              <span className="absolute left-9 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">/</span>
              <Input
                id="company-linkedin"
                value={linkedin}
                onChange={e => setLinkedin(e.target.value)}
                onBlur={() => setLinkedin(normalizeLinkedin(linkedin))}
                placeholder={t("forms.company.linkedinPlaceholder")}
                className="pl-[3.25rem]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-6 py-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={creating || enriching}>{showBackButton ? t("forms.actions.back") : t("common.cancel")}</Button>
        <Button onClick={handleSubmit} disabled={!name.trim() || creating || enriching}>
          {creating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("forms.actions.creating")}
            </>
          ) : (
            t("common.addEntity", { entity: singular.companies })
          )}
        </Button>
      </div>
    </>
  );
}
